import { setSpaceImages, setSpaceUsers, setUserDetail, setUserGroups, setUserGroupSpaces, setUserHubInfo, setCompanyLogo, setProfilePicture } from "../slices/userSlice.js";
import { setCompanyLogoUrl } from "../slices/companySlice.js";
import { setPlanDetails, setPlanLoading, setPlanError } from "../slices/planSlice.js";
import { getSpaceUsers, getSpaceImages, getUserInfoByEmail, getUsersGroup, fetchGroupsSpaces, getUserDetailsByEmail } from "../../services/WebServices.js";
import { getCompanyDetails, getCompanyLogoImage, getUserProfileImage } from "../../services/adminServices.js";
import { fetchCompanyPlan } from "../../services/planServices.js";
import { showLoader } from "../../utils/showloader.js";
import { redirectToLogin } from "../../utils/authRedirect.js";

export const fetchUserInfo = () => async (dispatch) => {
    try {
        // showLoader(true)
        const response = await getUserInfoByEmail();
        // showLoader(false)
        if (response.userId) {
            dispatch(setUserDetail(response));
        } else if (response.userNotFound) {
            // No profile behind this session — the app cannot render anything
            // useful, so warn and send the user back to login.
            redirectToLogin(response.message || "User not found");
        }
    } catch (error) {
    }

};

export const fetchUserGroup = (userEmail, userId) => async (dispatch) => {
    try {
        const response = await getUsersGroup(userEmail, userId);
        if (response.result) {
            dispatch(setUserGroups(response.result));
        } else {
            dispatch(setUserGroups([]));
        }
    }
    catch {
    }
}

export const fetchUserHubInfo = (email) => async (dispatch) => {
    try {
        const response = await getUserDetailsByEmail(email);
        if (response.user) {
            dispatch(setUserHubInfo(response.user))
        } else {
            dispatch(setUserHubInfo({}))
        }
    }
    catch {

    }
}
export const fetchSpaceImages = (spaceIds) => async (dispatch) => {
    try {
        const response = await getSpaceImages(spaceIds);
        dispatch(setSpaceImages(response));
    }
    catch {
    }
}

export const fetchSpaceForUserGroups = (userGroups, userId) => async (dispatch) => {
    // let groupIds = userGroups.
    let groupList = userGroups.map(group => group.groupId);
    try {
        const response = await fetchGroupsSpaces(groupList.join("#"));
        if (response.data?.result) {
            dispatch(setUserGroupSpaces(response.data.result));
        } else {
            dispatch(setUserGroupSpaces([]));
        }
    }
    catch {
    }
}

export const fetchSpaceUsers = (spaceId, userInfo) => async (dispatch) => {
    try {
        const response = await getSpaceUsers(spaceId, userInfo);
        if (response.data) {
            dispatch(setSpaceUsers(response.data));
        } else {
            dispatch(setSpaceUsers([]));
        }
    }
    catch {
    }
}

export const fetchCompanyLogo = (userId, companyName) => async (dispatch, getState) => {
    try {
        // Check if logo is already in company state (which the header uses)
        const { companyLogo } = getState().company;
        if (companyLogo) {
            return;
        }

        const normalizedCompanyName = (companyName ?? "").toString().trim();
        if (!normalizedCompanyName || normalizedCompanyName === "null" || normalizedCompanyName === "undefined") {
            dispatch(setCompanyLogo(null));
            dispatch(setCompanyLogoUrl(null));
            return;
        }

        const details = await getCompanyDetails(normalizedCompanyName, userId);
        const data = details?.data || {};

        // 1. Check if logo is directly returned as base64 data URI (new API format)
        if (data.logo && typeof data.logo === 'string' && data.logo.startsWith('data:')) {
            dispatch(setCompanyLogo(data.logo));
            dispatch(setCompanyLogoUrl(data.logo));
            return;
        }

        // 2. Fallback: check for logo as filename and fetch image
        // The API might use 'logo' or 'company_logo' for the filename
        const logoFileName = (data.logo || data.company_logo || "").toString().trim();
        const normalizedLogoFileName = logoFileName !== "null" && logoFileName !== "undefined" ? logoFileName : "";

        if (normalizedLogoFileName) {
            const img = await getCompanyLogoImage(userId, normalizedLogoFileName);
            if (img?.content) {
                const ext = (img.fileName || normalizedLogoFileName || "").split(".").pop()?.toLowerCase();
                const mime = ext === "jpg" || ext === "jpeg" ? "image/jpeg" : ext === "gif" ? "image/gif" : "image/png";
                const base64Logo = `data:${mime};base64,${img.content}`;
                dispatch(setCompanyLogo(base64Logo));
                dispatch(setCompanyLogoUrl(base64Logo));
                return;
            }
        }
        dispatch(setCompanyLogo(null));
        dispatch(setCompanyLogoUrl(null));
    } catch (error) {
        dispatch(setCompanyLogo(null));
        dispatch(setCompanyLogoUrl(null));
    }
}

export const clearCompanyLogo = () => (dispatch) => {
    dispatch(setCompanyLogo(null));
    dispatch(setCompanyLogoUrl(null));
}

export const updateCompanyLogo = (logoUrl) => (dispatch) => {
    dispatch(setCompanyLogo(logoUrl));
    dispatch(setCompanyLogoUrl(logoUrl));
}

/**
 * Fetch and cache the user's profile picture in Redux.
 * Mirrors the fetchCompanyLogo pattern — skips the API call if already cached.
 */
export const fetchProfilePicture = (userId, profilePictureFileName) => async (dispatch, getState) => {
    try {
        const { profilePicture } = getState().user;
        if (profilePicture) return;

        if (!userId || !profilePictureFileName) {
            dispatch(setProfilePicture(null));
            return;
        }

        const img = await getUserProfileImage(userId, profilePictureFileName);
        if (img?.content) {
            const ext = (img.fileName || profilePictureFileName || "").split(".").pop()?.toLowerCase();
            const mime = ext === "jpg" || ext === "jpeg" ? "image/jpeg" : ext === "gif" ? "image/gif" : "image/png";
            const url = `data:${mime};base64,${img.content}`;
            dispatch(setProfilePicture(url));
        } else {
            dispatch(setProfilePicture(null));
        }
    } catch (error) {
        dispatch(setProfilePicture(null));
    }
};

/** Call this after the user uploads a new profile picture. */
export const updateProfilePicture = (dataUrl) => (dispatch) => {
    dispatch(setProfilePicture(dataUrl));
};

/** Call this after the user deletes their profile picture. */
export const clearProfilePicture = () => (dispatch) => {
    dispatch(setProfilePicture(null));
};

/**
 * Fetch company plan limits (spaces, users, requests, etc.)
 * @param {string} companyName - Company name/identifier
 */
export const fetchPlanLimits = (companyName) => async (dispatch) => {
    if (!companyName) return;
    
    dispatch(setPlanLoading(true));
    try {
        const response = await fetchCompanyPlan(companyName);
        if (response.success && response.data) {
            dispatch(setPlanDetails({
                plan: response.data.plan,
                features: response.data.features
            }));
        } else {
            dispatch(setPlanError(response.message || "Failed to fetch plan limits"));
        }
    } catch (error) {
        dispatch(setPlanError(error.message || "Error fetching plan limits"));
    } finally {
        dispatch(setPlanLoading(false));
    }
}